<script>
  import Section from '../components/Section.svelte';
  import Panel from '../components/Panel.svelte';
  import Button from '../components/Button.svelte';
  import Async from '../components/Async.svelte';
  import QueryPanel from '../components/QueryPanel.svelte';
  import HelpDrawer from '../components/HelpDrawer.svelte';
  import { runAnalysis, getDatasets } from '../lib/api.js';
  import { HELP } from '../lib/help.js';
  import { can } from '../lib/session.svelte.js';
  import { queryDraft } from '../lib/queryDraft.svelte.js';
  import { go } from '../lib/nav.svelte.js';

  // Saving a query is the analyst's (the backend enforces it); Edit opens the
  // query where it was made, on Queries.
  const canEdit = $derived(can('author-queries'));
  function edit(query) {
    queryDraft.entry = query;
    go('queries');
  }

  let runId = $state(0);
  // Starts a background job on the backend and polls it to completion. The
  // job loads queries.json from the Pod and runs every entry there, under the
  // institutional credential — the panels below are composed from whatever the
  // Pod holds, not from code.
  const analysis = $derived.by(() => { runId; return Promise.all([runAnalysis(), getDatasets()])
    .then(([a, { datasets }]) => ({ ...a, datasets })); });

  // Group saved comparisons by the dataset they compare, in the datasets'
  // order; one that names none comes last. No query ids or names appear in
  // this file.
  function byDataset(queries, datasets) {
    const groups = [...datasets.map((d) => ({ title: d.label, queries: queries.filter((q) => q.dataset === d.id) })),
      { title: 'Other comparisons', queries: queries.filter((q) => !datasets.some((d) => d.id === q.dataset)) }];
    return groups.filter((g) => g.queries.length);
  }
</script>

<HelpDrawer {...HELP.analysis} />

<h1 class="wf2-title wf2-title-level-1">Comparisons</h1>
<p class="wf2-subtitle">Read each institution's figures side by side: one panel per comparison, one column per institution.</p>

<Panel>
  <p class="wf2-text">Run again to pick up anything shared since you last looked.</p>
  <div class="wf2-row" style="gap:12px">
    <Button variant="primary" onclick={() => (runId += 1)}>Run again</Button>
    {#if canEdit}<Button variant="secondary" to="queries">Add new</Button>{/if}
  </div>
</Panel>

<Async promise={analysis} loading="Reading the shared figures…">
  {#snippet children(a)}
    <p class="wf2-caption">
      Ran {a.queries.length} comparison{a.queries.length === 1 ? '' : 's'} over the
      {a.counts.ownFiles} counts you share and the
      {a.counts.partnerFiles} that {a.partner.label} shares with you.
    </p>

    {#if !a.queries.length}
      <Section title="Nothing saved yet">
        <Panel>
          <p class="wf2-text">
            No comparisons saved yet.{#if canEdit} Use <a href="#/queries">Add new</a> to make one.{/if}
          </p>
        </Panel>
      </Section>
    {/if}

    {#each byDataset(a.queries, a.datasets) as { title, queries }}
      <Section {title}>
        {#each queries as q}
          <QueryPanel
            query={q}
            result={a.results[q.id] ?? { error: 'No result returned for this query.' }}
            publishers={a.publishers ?? []}
            periodsAddUp={a.periodsAddUp ?? true}
            onedit={canEdit ? edit : null}
          />
        {/each}
      </Section>
    {/each}

  {/snippet}
</Async>
